import { useRef, useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref, getDownloadURL, uploadBytes, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentWeekRates, toGBP, getRateForCurrency } from '../utils/exchangeRates';
import { useExpenseCategories } from '../utils/expenseCategories';

const MAX_BYTES = 20 * 1024 * 1024;
const INPUT_CLASS = 'w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white';

function today() {
  return new Date().toISOString().split('T')[0];
}

function isAcceptedFile(file) {
  return file.type === 'application/pdf' || file.type.startsWith('image/');
}

function Spinner({ label }) {
  return (
    <div className="flex items-center gap-3 py-6">
      <svg className="w-5 h-5 animate-spin text-pb flex-shrink-0" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
      <span className="text-[13px] text-[#374151]">{label}</span>
    </div>
  );
}

// Upload an invoice from an Expenses screen. Asks whether it should be read (its
// figures then count as an expense, once staff have checked them) or just stored
// (kept on file as a `storedOnly` expense with no amount, so it counts in no totals).
//
// Pass `tour` ({ requestId, requestSummary }) on a tour's own tab, or `requests`
// to let staff pick the tour. `extraFields` are added to the saved expense.
export default function InvoiceUpload({ tour, requests, extraFields, onSaved, onClose }) {
  const { currentUser } = useAuth();
  const expenseCategories = useExpenseCategories();
  const fileInputRef = useRef(null);

  const [phase, setPhase] = useState('pick'); // pick | choose | uploading | reading | form
  const [mode, setMode] = useState(null); // read | store
  const [file, setFile] = useState(null);
  const [uploaded, setUploaded] = useState(null); // { path, url }
  const [form, setForm] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  function set(key, val) {
    setForm((p) => ({ ...p, [key]: val }));
  }

  function handleFile(e) {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    if (!isAcceptedFile(picked)) { setError('Please choose a PDF or an image.'); return; }
    if (picked.size >= MAX_BYTES) { setError('That file is too large — the limit is 20MB.'); return; }
    setError('');
    setFile(picked);
    setPhase('choose');
  }

  function blankForm() {
    return {
      date: today(),
      category: expenseCategories.includes('Other') ? 'Other' : expenseCategories[0],
      description: file.name.replace(/\.[^.]+$/, ''),
      amount: '',
      currency: 'GBP',
      paymentStatus: 'to_pay',
      requestId: tour?.requestId || '',
      requestSummary: tour?.requestSummary || '',
    };
  }

  async function handleChoice(choice) {
    setMode(choice);
    setError('');
    setNotice('');
    setPhase('uploading');

    let stored = uploaded;
    if (!stored) {
      try {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-80);
        const path = `expenseInvoices/${currentUser.uid}/${Date.now()}-${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file, { contentType: file.type });
        stored = { path, url: await getDownloadURL(storageRef) };
        setUploaded(stored);
      } catch (err) {
        console.error('Invoice upload error:', err);
        setError('The invoice could not be uploaded. Please try again.');
        setPhase('choose');
        return;
      }
    }

    if (choice === 'store') {
      setForm(blankForm());
      setPhase('form');
      return;
    }

    setPhase('reading');
    try {
      const scanFn = httpsCallable(functions, 'scanReceipt');
      const { data } = await scanFn({ kind: 'invoice', storagePath: stored.path });
      setForm({
        ...blankForm(),
        date: data.date || today(),
        category: expenseCategories.includes(data.category) ? data.category : blankForm().category,
        description: data.description || blankForm().description,
        amount: data.amount != null ? String(data.amount) : '',
        currency: data.currency || 'GBP',
        paymentStatus: data.alreadyPaid ? 'paid' : 'to_pay',
      });
      const missing = [
        data.date == null && 'date',
        data.amount == null && 'amount',
        data.currency == null && 'currency',
      ].filter(Boolean);
      setNotice(missing.length
        ? `Invoice read, but we couldn't confirm the ${missing.join(', ')} — please check these carefully.`
        : 'Invoice read — check the details below, then save.');
    } catch (err) {
      console.error('Invoice read error:', err);
      setForm(blankForm());
      setNotice("We couldn't read this invoice. Enter the figures yourself, or just store it.");
    }
    setPhase('form');
  }

  function handleRequestChange(e) {
    const val = e.target.value;
    if (val === 'misc') { setForm((p) => ({ ...p, requestId: 'misc', requestSummary: 'MISC' })); return; }
    const req = requests.find((r) => r.id === val);
    const sportDest = req ? `${req.sport || ''}${req.destination ? ` — ${req.destination}` : ''}` : '';
    setForm((p) => ({ ...p, requestId: val, requestSummary: req ? (req.internalRef || sportDest) : '' }));
  }

  async function handleSave(e) {
    e.preventDefault();
    const storing = mode === 'store';
    if (!form.date || !form.description.trim() || (!storing && !form.amount)) {
      setError(storing ? 'Date and description are required.' : 'Date, amount and description are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const common = {
        ...extraFields,
        date: form.date,
        category: form.category,
        description: form.description.trim(),
        requestId: form.requestId,
        requestSummary: form.requestSummary,
        receiptUrl: uploaded.url,
        invoiceFileName: file.name,
        invoiceStoragePath: uploaded.path,
        createdAt: serverTimestamp(),
        createdBy: currentUser.email,
      };
      if (storing) {
        await addDoc(collection(db, 'expenses'), {
          ...common,
          storedOnly: true,
          paymentStatus: 'stored',
          amount: null,
          currency: null,
          gbpEquivalent: null,
        });
      } else {
        const rates = await getCurrentWeekRates();
        const amount = Number(form.amount);
        await addDoc(collection(db, 'expenses'), {
          ...common,
          paymentStatus: form.paymentStatus,
          amount,
          currency: form.currency,
          gbpEquivalent: toGBP(amount, form.currency, rates),
          exchangeRateUsed: getRateForCurrency(form.currency, rates),
          rateWeek: rates.weekStart,
        });
      }
      onSaved?.(form);
      onClose();
    } catch (err) {
      console.error('Save invoice error:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  // Nothing has been saved against the uploaded file, so don't leave it behind.
  function handleCancel() {
    if (uploaded) deleteObject(ref(storage, uploaded.path)).catch(() => {});
    onClose();
  }

  return (
    <div className="p-5 bg-surface rounded-[10px] border border-[#E5E7EB]">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-[14px] font-semibold text-[#111827]">Upload Invoice</h3>
          <p className="text-[12px] text-[#6B7280] mt-0.5">{file ? file.name : 'PDF or image, up to 20MB.'}</p>
        </div>
        <button type="button" onClick={handleCancel} className="text-[12px] text-[#6B7280] hover:text-[#374151] ml-4 flex-shrink-0">
          Cancel
        </button>
      </div>

      {phase === 'pick' && (
        <>
          <input ref={fileInputRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={handleFile} />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full py-8 border-2 border-dashed border-[#D1D5DB] rounded-[10px] text-[13px] font-semibold text-[#374151] hover:border-pb hover:text-pb transition-colors"
          >
            Choose invoice file
          </button>
        </>
      )}

      {phase === 'choose' && (
        <div>
          <p className="text-[13px] font-semibold text-[#111827] mb-3">
            Do you want this invoice read and the figures included on your expenses, or is this just to be stored?
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => handleChoice('read')}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition">
              Read it and add the figures
            </button>
            <button type="button" onClick={() => handleChoice('store')}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-[#374151] border border-[#E5E7EB] hover:border-pb/40 hover:text-pb bg-white rounded-[8px] transition">
              Just store it
            </button>
          </div>
        </div>
      )}

      {phase === 'uploading' && <Spinner label="Uploading invoice…" />}
      {phase === 'reading' && <Spinner label="Reading invoice with AI…" />}

      {phase === 'form' && (
        <form onSubmit={handleSave}>
          {mode === 'read' && notice && (
            <div className="mb-4 p-3 bg-amber-light border border-ba/25 rounded-[8px] flex flex-wrap items-center justify-between gap-2">
              <p className="text-[12px] text-[#92400E]">{notice}</p>
              <button type="button" onClick={() => { setMode('store'); setError(''); }}
                className="text-[12px] font-semibold text-pb hover:text-navy">
                Just store it instead
              </button>
            </div>
          )}
          {mode === 'store' && (
            <p className="mb-4 text-[12px] text-[#6B7280]">Kept on file with your expenses. It won't count towards any totals.</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Invoice date <span className="text-dr">*</span></label>
              <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className={INPUT_CLASS} />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Category</label>
              <select value={form.category} onChange={(e) => set('category', e.target.value)} className={INPUT_CLASS}>
                {expenseCategories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {mode === 'read' && (
              <div>
                <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Amount <span className="text-dr">*</span></label>
                <div className="flex gap-2">
                  <select value={form.currency} onChange={(e) => set('currency', e.target.value)}
                    className="w-20 border border-[#E5E7EB] rounded-[8px] px-2 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
                    <option>GBP</option><option>EUR</option><option>USD</option>
                  </select>
                  <input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => set('amount', e.target.value)}
                    placeholder="0.00" className={INPUT_CLASS} />
                </div>
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Description <span className="text-dr">*</span></label>
              <input type="text" value={form.description} onChange={(e) => set('description', e.target.value)} className={INPUT_CLASS} />
            </div>
            {requests && (
              <div>
                <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Linked Tour</label>
                <select value={form.requestId} onChange={handleRequestChange} className={INPUT_CLASS}>
                  <option value="">— Select tour or MISC —</option>
                  <option value="misc">MISC (not tour-specific)</option>
                  {[...requests]
                    .sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || ''))
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.internalRef || `${r.sport || '?'}${r.destination ? ` — ${r.destination}` : ''}${r.departureDate ? ` (${r.departureDate})` : ''}`}
                      </option>
                    ))}
                </select>
              </div>
            )}
            {mode === 'read' && (
              <div>
                <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Payment Status</label>
                <div className="flex gap-2">
                  {['to_pay', 'paid'].map((s) => (
                    <button key={s} type="button" onClick={() => set('paymentStatus', s)}
                      className={`px-3 py-2 text-[12px] font-bold rounded-[8px] transition ${form.paymentStatus === s ? (s === 'paid' ? 'bg-green-light text-sg' : 'bg-amber-light text-[#92400E]') : 'bg-white border border-[#E5E7EB] text-[#9CA3AF]'}`}>
                      {s === 'paid' ? 'Paid' : 'To Pay'}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button type="submit" disabled={saving}
            className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
            {saving ? 'Saving…' : mode === 'store' ? 'Store invoice' : 'Save expense'}
          </button>
        </form>
      )}

      {error && <p className="text-[12px] text-dr mt-2">{error}</p>}
    </div>
  );
}
